import { useEffect, useMemo, useRef, useState } from 'react';

import Modal from '@/components/Modal';
import ThemedIconButton from '@/components/ThemedIconButton';
import { setShareModalOpen } from '@/state/features/app';
import { useAppDispatch, useAppSelector } from '@/state/store';

import { sendEventV2 } from '@/utils/analytics';
import { copyToClipboard } from '@/utils/dom';
import { isDiscordOnMobile } from '@/components/DiscordAuthFlow/useDiscord';
import { IS_DISCORD_ACTIVITY } from '@/globals';
import { useUITranslatedText } from '@/components/TranslatedText';

import * as styles from './styles.module.scss';

export default function ShareModal() {
    const dispatch = useAppDispatch();
    const shareModalOpen = useAppSelector((state) => state.app.shareModalOpen);
    const { message: shareMessage = '', disableAutoLink } = useAppSelector((state) => state.app.shareData);

    // Translations
    const translation_share = useUITranslatedText('share', 'Share');
    const translation_copy = useUITranslatedText('copy', 'Copy');
    const translation_copied = useUITranslatedText('copied', 'Copied!');

    const [hasCopied, setHasCopied] = useState(false);
    const copyTextTimeout = useRef<number>(0);

    const shareURL = disableAutoLink ? '' : window.location.origin;

    const url = encodeURIComponent(shareURL);
    const title = encodeURIComponent(shareMessage);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&title=${title}`;
    const twitterUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;

    function open(url: string) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    // Track share button click
    function trackShare(target: string) {
        sendEventV2('share', [target, shareMessage, shareURL]);
    }

    useEffect(() => {
        if (shareModalOpen) {
            sendEventV2('arise_share_modal_open');
        }
    }, [shareModalOpen]);

    const isDiscordMobile = useMemo(() => {
        try {
            return IS_DISCORD_ACTIVITY && isDiscordOnMobile();
        } catch (error) {
            return false;
        }
    }, []);

    return (
        <Modal
            title={translation_share}
            onClose={() => {
                dispatch(setShareModalOpen(false));
            }}
            isVisible={shareModalOpen}
            zIndex={130}
        >
            <div className={styles.buttonContainer}>
                {!isDiscordMobile && (
                    <>
                        <ThemedIconButton
                            text={translation_share}
                            onClick={() => {
                                trackShare('facebook');
                                open(facebookUrl);
                            }}
                            disabled={false}
                            icon="facebook"
                        />
                        <ThemedIconButton
                            text={translation_share}
                            onClick={() => {
                                trackShare('twitter');
                                open(twitterUrl);
                            }}
                            disabled={false}
                            icon="twitter"
                        />
                    </>
                )}
                <ThemedIconButton
                    text={hasCopied ? translation_copied : translation_copy}
                    onClick={async () => {
                        const success = await copyToClipboard(`${shareMessage} ${shareURL}`);
                        if (success) {
                            setHasCopied(true);
                            clearTimeout(copyTextTimeout.current);
                            copyTextTimeout.current = setTimeout(() => {
                                setHasCopied(false);
                            }, 2000) as any as number;
                            trackShare('copy');
                        }
                    }}
                    disabled={false}
                    icon="copy"
                />
            </div>
        </Modal>
    );
}
